import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown, Droplets, Building2 } from "lucide-react";
import { ASSETS } from "@/data/site";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/products", label: "Products", testid: "nav-link-products" },
  { to: "/partnership", label: "Partnership", testid: "nav-link-partnership" },
];

const ABOUT_OPTIONS = [
  {
    to: "/about",
    title: "Ananya Ventures",
    sub: "Foods & beverages — our brand story",
    icon: Droplets,
    testid: "about-option-ventures",
  },
  {
    to: "/consumer-goods",
    title: "Ananya Consumer Goods Pvt. Ltd.",
    sub: "Personal & home care company",
    icon: Building2,
    testid: "about-option-consumer-goods",
  },
];

const ABOUT_PATHS = ["/about", "/consumer-goods"];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const location = useLocation();
  const aboutActive = ABOUT_PATHS.includes(location.pathname);

  const closeAll = () => {
    setOpen(false);
    setAboutOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 pt-4">
      <nav className="mx-auto max-w-6xl flex items-center justify-between rounded-2xl border border-white/10 bg-navy/70 backdrop-blur-xl px-4 sm:px-6 py-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3" onClick={closeAll}>
          <span className="bg-white rounded-full px-2.5 py-1 sm:px-4 sm:py-1.5 flex items-center gap-2 sm:gap-3 shadow-lg">
            <img src={ASSETS.logo} alt="Ananya Refresh" className="h-6 sm:h-8 w-auto" />
            <span className="w-px h-4 sm:h-6 bg-slate-300" aria-hidden="true" />
            <img src={ASSETS.logoGold} alt="Ananya Gold" className="h-6 sm:h-8 w-auto" />
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                  isActive ? "text-sun-bright" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-active"
                      className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-lime"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}

          <div
            className="relative"
            onMouseEnter={() => setAboutOpen(true)}
            onMouseLeave={() => setAboutOpen(false)}
          >
            <button
              data-testid="nav-about-us-toggle"
              onClick={() => setAboutOpen(!aboutOpen)}
              className={`relative flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                aboutActive ? "text-sun-bright" : "text-slate-300 hover:text-white"
              }`}
            >
              About Us
              <ChevronDown size={14} className={`transition-transform duration-300 ${aboutOpen ? "rotate-180" : ""}`} />
              {aboutActive && (
                <motion.span
                  layoutId="nav-active"
                  className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-lime"
                />
              )}
            </button>

            <AnimatePresence>
              {aboutOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute right-0 top-full pt-3 w-80"
                  data-testid="nav-about-menu"
                >
                  <div className="rounded-2xl border border-white/10 bg-navy-card/95 backdrop-blur-xl p-2 shadow-2xl shadow-black/50">
                    {ABOUT_OPTIONS.map((o) => (
                      <Link
                        key={o.to}
                        to={o.to}
                        data-testid={o.testid}
                        onClick={() => setAboutOpen(false)}
                        className={`group flex items-center gap-4 rounded-xl px-4 py-3.5 transition-colors duration-300 ${
                          location.pathname === o.to ? "bg-lime/10" : "hover:bg-white/5"
                        }`}
                      >
                        <span className={`rounded-full p-2.5 transition-colors duration-300 ${
                          location.pathname === o.to ? "bg-lime text-navy" : "bg-sun/10 text-sun-bright group-hover:bg-lime group-hover:text-navy"
                        }`}>
                          <o.icon size={17} />
                        </span>
                        <span>
                          <span className={`block text-sm font-bold ${
                            location.pathname === o.to ? "text-lime" : "text-white"
                          }`}>
                            {o.title}
                          </span>
                          <span className="block text-xs text-slate-400 mt-0.5">{o.sub}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/partnership"
            data-testid="nav-distributor-cta"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-lime px-5 py-2.5 text-sm font-bold text-navy transition-all duration-300 hover:bg-sun-bright hover:gap-2.5"
          >
            Become a Distributor <ArrowUpRight size={16} />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="md:hidden mx-auto max-w-6xl mt-2 rounded-2xl border border-white/10 bg-navy/95 backdrop-blur-xl p-4"
            data-testid="nav-mobile-menu"
          >
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                onClick={closeAll}
                className={`block px-4 py-3 rounded-xl text-base font-medium ${
                  location.pathname === l.to ? "text-sun-bright bg-white/5" : "text-slate-300"
                }`}
              >
                {l.label}
              </NavLink>
            ))}
            <p className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
              About Us
            </p>
            {ABOUT_OPTIONS.map((o) => (
              <NavLink
                key={o.to}
                to={o.to}
                data-testid={`${o.testid}-mobile`}
                onClick={closeAll}
                className={`block px-4 py-3 rounded-xl text-base font-medium ${
                  location.pathname === o.to ? "text-sun-bright bg-white/5" : "text-slate-300"
                }`}
              >
                {o.title}
              </NavLink>
            ))}
            <Link
              to="/partnership"
              data-testid="nav-distributor-cta-mobile"
              onClick={closeAll}
              className="mt-2 flex items-center justify-center gap-1.5 rounded-full bg-lime px-5 py-3 text-sm font-bold text-navy"
            >
              Become a Distributor <ArrowUpRight size={16} />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
